import { API_BASE_URL } from "./client";

export type TenantFile = {
  id: number;
  tenant_id: number;
  tenant_name: string;
  file_name: string;
  file_type: string;
  file_size: number;
  uploaded_at: string;
  uploaded_by_name: string | null;
};

export async function getTenantFiles(
  token: string,
  filters: { tenantId?: number; month?: string } = {},
) {
  const params = new URLSearchParams();
  if (filters.tenantId) params.set("tenant_id", String(filters.tenantId));
  if (filters.month) params.set("month", filters.month);
  const query = params.toString();
  const response = await fetch(
    `${API_BASE_URL}/api/admin/files${query ? `?${query}` : ""}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) throw new Error("Failed to fetch tenant files");
  return response.json() as Promise<TenantFile[]>;
}

export async function uploadTenantFile(
  token: string,
  tenantId: number,
  file: File,
) {
  const formData = new FormData();
  formData.append("tenant_id", String(tenantId));
  formData.append("file", file);
  const response = await fetch(`${API_BASE_URL}/api/admin/files`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    let message = "Upload failed";
    try {
      message = (await response.json())?.error || message;
    } catch {
      // Keep the generic message for non-JSON errors.
    }
    throw new Error(message);
  }
  return response.json() as Promise<TenantFile>;
}

export async function deleteTenantFile(token: string, fileId: number) {
  const response = await fetch(`${API_BASE_URL}/api/admin/files/${fileId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("Failed to delete file");
}

export async function openTenantFile(token: string, fileId: number) {
  const response = await fetch(
    `${API_BASE_URL}/api/admin/files/file/${fileId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!response.ok) throw new Error("Unable to open file");
  const blobUrl = URL.createObjectURL(await response.blob());
  window.open(blobUrl, "_blank", "noopener,noreferrer");
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

export async function downloadTenantFile(
  token: string,
  fileId: number,
  fileName: string,
) {
  const response = await fetch(
    `${API_BASE_URL}/api/admin/files/file/${fileId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!response.ok) throw new Error("Unable to download file");

  const blobUrl = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}
